import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { createLogger } from '../utils/logger';

const logger = createLogger('PWAUpdate');

// PWA 관련 타입 정의
interface ServiceWorkerRegistration {
  update: () => void;
}

interface PWARegisterOptions {
  onRegistered?: (registration: ServiceWorkerRegistration | null) => void;
  onRegisterError?: (error: any) => void;
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
}

interface PWARegisterReturn {
  needRefresh: boolean;
  offlineReady: boolean;
  updateServiceWorker: (reloadPage?: boolean) => Promise<void>;
}

// virtual:pwa-register/react 모듈 fallback
const useRegisterSW = (options: PWARegisterOptions): PWARegisterReturn => {
  const [needRefresh, setNeedRefresh] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    // 네이티브(Capacitor) 앱에서는 PWA 서비스 워커가 불필요하며,
    // 앱 업데이트(install -r) 시 스테일 캐시로 인한 크래시("... is not defined")를 유발하므로
    // 기존 등록을 해제하고 새로 등록하지 않는다.
    if (Capacitor.isNativePlatform()) {
      navigator.serviceWorker.getRegistration().then((r) => r?.unregister());
      return;
    }

    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        options.onRegistered?.(registration);
        
        // 업데이트 확인
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setNeedRefresh(true);
                options.onNeedRefresh?.();
              }
            });
          }
        });
      })
      .catch((error) => {
        options.onRegisterError?.(error);
      });

    // 오프라인 상태 확인
    if (!navigator.onLine) {
      setOfflineReady(true);
      options.onOfflineReady?.();
    }
  }, []);

  const updateServiceWorker = async (reloadPage = false) => {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        if (reloadPage) {
          window.location.reload();
        }
      }
    }
  };

  return {
    needRefresh,
    offlineReady,
    updateServiceWorker
  };
};

interface PWAUpdateState {
  needRefresh: boolean;
  offlineReady: boolean;
  updateAvailable: boolean;
  isUpdating: boolean;
}

export const usePWAUpdate = () => {
  const [updateState, setUpdateState] = useState<PWAUpdateState>({
    needRefresh: false,
    offlineReady: false,
    updateAvailable: false,
    isUpdating: false
  });

  const [showUpdateNotification, setShowUpdateNotification] = useState(false);
  const [lastUpdateCheck, setLastUpdateCheck] = useState<number>(0);

  const {
    needRefresh,
    offlineReady,
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r: ServiceWorkerRegistration | null) {
      logger.info('SW Registered: ' + r);
      // 등록 후 주기적으로 업데이트 확인
      if (r) {
        setInterval(() => {
          r.update();
        }, 60000); // 1분마다 업데이트 확인
      }
    },
    onRegisterError(error: any) {
      logger.debug('SW registration error', error);
    },
    onNeedRefresh() {
      logger.info('New version available');
      setUpdateState(prev => ({ 
        ...prev, 
        needRefresh: true, 
        updateAvailable: true 
      }));
      setShowUpdateNotification(true);
    },
    onOfflineReady() {
      logger.info('App ready to work offline');
      setUpdateState(prev => ({ 
        ...prev, 
        offlineReady: true 
      }));
    },
  });

  // 버전 체크 함수
  const checkForUpdates = useCallback(async () => {
    const now = Date.now();
    
    // 마지막 체크로부터 30초 이상 지났을 때만 체크
    if (now - lastUpdateCheck < 30000) {
      return;
    }
    
    setLastUpdateCheck(now);
    
    try {
      // 현재 빌드 타임스탬프 확인
      const currentTimestamp = document.querySelector('meta[name="build-timestamp"]')?.getAttribute('content');
      
      if (currentTimestamp) {
        // 서버에서 최신 빌드 타임스탬프 확인
        const response = await fetch(`/index.html?t=${now}`, {
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (response.ok) {
          const html = await response.text();
          const serverTimestampMatch = html.match(/name="build-timestamp" content="([^"]+)"/);
          
          if (serverTimestampMatch && serverTimestampMatch[1] !== currentTimestamp) {
            logger.info('New version detected:', serverTimestampMatch[1], 'vs current:', currentTimestamp);
            setUpdateState(prev => ({ 
              ...prev, 
              needRefresh: true, 
              updateAvailable: true 
            }));
            setShowUpdateNotification(true);
          }
        }
      }
    } catch (error) {
      console.error('Failed to check for updates:', error);
    }
  }, [lastUpdateCheck]);

  // 업데이트 실행
  const performUpdate = useCallback(async () => {
    setUpdateState(prev => ({ ...prev, isUpdating: true }));
    setShowUpdateNotification(false);
    
    try {
      await updateServiceWorker(true);
      
      // 추가 캐시 클리어
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }
      
      // 페이지 새로고침
      window.location.reload();
    } catch (error) {
      console.error('Update failed:', error);
      setUpdateState(prev => ({ ...prev, isUpdating: false }));
      setShowUpdateNotification(true);
    }
  }, [updateServiceWorker]);

  // 업데이트 알림 닫기
  const dismissUpdate = useCallback(() => {
    setShowUpdateNotification(false);
    setUpdateState(prev => ({ ...prev, needRefresh: false }));
  }, []);

  // 페이지 포커스 시 업데이트 확인
  useEffect(() => {
    const handleFocus = () => {
      checkForUpdates();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForUpdates();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 초기 체크
    checkForUpdates();

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdates]);

  return {
    ...updateState,
    showUpdateNotification,
    performUpdate,
    dismissUpdate,
    checkForUpdates
  };
}; 